import React, { BaseSyntheticEvent, useState } from 'react';
import { User, removeUserFromStudy } from '../../api/studykit';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText } from '@dls/react-core';
import { useSnackbar } from 'notistack';
import { NotiStackAction } from '../styled/notistack-action';
import {
  StyledDialogHeading,
  StyledDialogIconContainer,
  StyledDialogTitleForIcon,
} from '../styled/styles';
import { Icon } from '@dls/react-icon';
import { ColorX11 } from '../../styling/color';
import { ServerValidationError } from '../../api/axios';

interface RemoveUserDialogProps {
  studyId: string;
  user?: User;
  open: boolean;
  onClose: () => void;
  onRemoved: (u: User) => void;
}

export const RemoveUserDialog: React.FC<RemoveUserDialogProps> = ({
  studyId,
  user,
  open,
  onClose,
  onRemoved,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const onSubmit = (e: BaseSyntheticEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsSubmitting(true);
    removeUserFromStudy(studyId, user.email)
      .then(() => onRemoved(user))
      .then(() => {
        enqueueSnackbar(`${user.email || 'User'} successfully removed!`, {
          variant: 'success',
          action: <NotiStackAction close={closeSnackbar} />,
        });
        onClose();
      })
      .catch((error) => {
        let message = `Error removing ${user.email || 'user'}!`;
        if (error instanceof ServerValidationError) {
          message = error.message;
        }
        enqueueSnackbar(message, {
          variant: 'error',
          action: <NotiStackAction close={closeSnackbar} />,
        });
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <StyledDialogTitleForIcon disableTypography>
        <StyledDialogIconContainer>
          <Icon name={'DeleteTrash'} size={'m'} color={ColorX11.Black} />
        </StyledDialogIconContainer>
        <StyledDialogHeading variant={'h3'}>Are you sure?</StyledDialogHeading>
      </StyledDialogTitleForIcon>
      <DialogContent>
        <DialogContentText>Are you sure you want to remove the following user?</DialogContentText>
        <br />
        <DialogContentText>{user?.email || ''}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <form onSubmit={onSubmit}>
          <Button
            disabled={isSubmitting}
            type={'button'}
            onClick={onClose}
            dls_variant={'quietEmphasis'}
          >
            Cancel
          </Button>
          <Button
            disabled={isSubmitting}
            style={{ marginLeft: `8px` }}
            type={'submit'}
            dls_variant={'signalError'}
          >
            Remove
          </Button>
        </form>
      </DialogActions>
    </Dialog>
  );
};
